import React, { ChangeEvent, useCallback, useEffect, useState } from "react";
import { ContentWrapper, ContinueButton, Form, InputBox, ItemWrapper } from "../styles";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import CustomSelect from "../../../../components/common/CustomSelect";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { useBankList } from "../../../../app/hooks/useBankList";
import { Input } from "../../../../components/common/Input";
import { Button } from "../../../../components/common/Button";
import { savedPayoutV2Data } from "../../redux/formDataSlice";
import Loader from "../../../../components/common/Loader";
import { getBankAccountDetail } from "../../api";
import { standardizeBankCode, sanitizeAccountNumberInput, NGN_ACCOUNT_NUMBER_LENGTH } from "../../../../utils/helpers";

type SelectedBankObject = {
    label: string;
    value: string;
    originalCode?: string;
};

type NGNBeneficiaryTypes = {
    handleNext?: () => void;
    handlePrevious?: () => void;
    paymentMethod?: string;
    paymentMethodName?: string;
    bankName?: string;
    accountNumber?: string;
    accountName?: string;
    paymentDescription?: string;
    bankCode?: string;
    selectedBank?: SelectedBankObject;
    accountType?: string;
    description?: string;
    IdentityDoc?: string;
    identityNumber?: string;
    recipientAddress?: string;
    recipientEmail?: string;
    phoneNumber?: string;
    identityType?: string;
};

const Beneficiary = ({ handleNext, handlePrevious }: NGNBeneficiaryTypes) => {

    const getSavedData = useAppSelector((state) => state.formData.payoutv2Data);

    const {
        handleSubmit,
        control,
        formState: { errors, isValid },
        setValue,
        setError: setFormError,
        clearErrors,
        watch
    } = useForm<NGNBeneficiaryTypes>({
        defaultValues: getSavedData,
        mode: "onChange",
    });

    const dispatch = useAppDispatch();

    const watchAccountNumber = watch("accountNumber")
    const watchBankName = watch("bankName")

    const { businessId } = useAppSelector((state) => state.users);

    const [userData, setUserData] = useState<NGNBeneficiaryTypes>();
    const [loading, setLoading] = useState(false)

    const country = "NGN"
    const { selectBanks } = useBankList({ country, businessId })

    const handleDataUpdate = (field: keyof NGNBeneficiaryTypes, value: any) => {
        setUserData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleBankChange = (selectedModifiedValue) => {
        const originalBankCode = standardizeBankCode(selectedModifiedValue);

        const selectedBank = selectBanks.find((b) => b.value === selectedModifiedValue)

        setValue("bankCode", originalBankCode);
        handleDataUpdate("bankName", selectedModifiedValue);
        handleDataUpdate("selectedBank", selectedBank);
        handleDataUpdate("bankCode", originalBankCode)
    };

    useEffect(() => {
        async function fetchData() {
            setLoading(true);
            try {

                await getBankAccountDetail({
                    accountNumber: watchAccountNumber,
                    bankCode: standardizeBankCode(watchBankName),
                    countryCode: "NG",
                }).then((data) => {
                    setValue("accountName", data.account_name, {
                        shouldValidate: true,
                    })
                    clearErrors("accountNumber")
                });
            } catch (error) {
                setFormError("accountNumber", {
                    type: "server",
                    message: error?.response?.data?.message ?? "Error fetching account details.",
                });
            } finally {
                setLoading(false);
            }
        }

        if (watchBankName && watchAccountNumber?.length === NGN_ACCOUNT_NUMBER_LENGTH) {
            fetchData();
        }
    }, [watchAccountNumber, watchBankName]);

    const onSubmit: SubmitHandler<NGNBeneficiaryTypes> = useCallback(
        (data) => {
            const selectedBank = selectBanks?.find((b) => b.value === data.bankName)

            const combinedData = {
                ...userData,
                bankName: data.bankName,
                bankCode: selectedBank?.originalCode,
                accountNumber: data.accountNumber,
                accountName: data.accountName,
                description: data.description,
                selectedBank,
            };
            dispatch(savedPayoutV2Data(combinedData));
            handleNext?.();
        },
        [dispatch, userData, selectBanks, handleNext],
    );

    return (
        <ContentWrapper>
            <Loader isLoading={loading} />
            <Form>
                <InputBox>
                    <Controller
                        name="bankName"
                        control={control}
                        rules={{
                            required: "Please input the beneficiary's Bank name",
                        }}
                        render={({ field }) => (
                            <CustomSelect
                                placeholder={"Select bank"}
                                $width="100%"
                                field={field}
                                disableLabel
                                options={selectBanks}
                                onChange={handleBankChange}
                                showSearch
                                filterOption={(inputValue, option) =>
                                    option.label.toLowerCase().includes(inputValue.toLowerCase())
                                }
                                lightBorder
                            />
                        )}
                    />
                    <span className={"error"}>
                        {errors.bankName?.message}
                    </span>
                </InputBox>

                <InputBox>
                    <Controller
                        name="accountNumber"
                        control={control}
                        rules={{
                            required: "Please enter the beneficiary's account number",
                            pattern: {
                                value: /^\d{10}$/,
                                message: "Account number must be 10 digits",
                            },
                        }}
                        render={({ field: { onChange, value, ...field } }) => (
                            <Input
                                type="text"
                                inputMode="numeric"
                                label={"Account number"}
                                labelColor="#FFFFFF"
                                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                    onChange(
                                        sanitizeAccountNumberInput(
                                            e.target.value,
                                            NGN_ACCOUNT_NUMBER_LENGTH,
                                        ),
                                    );
                                }}
                                value={value}
                                {...field}
                                disableLabel
                            />
                        )}
                    />
                    <span className={"error"}>
                        {errors.accountNumber?.message}
                    </span>

                </InputBox>

                <InputBox>
                    <Controller
                        name="accountName"
                        control={control}
                        rules={{
                            required: "Please enter the beneficiary's account name",
                        }}
                        render={({ field }) => (
                            <Input
                                type="text"
                                label="Account name"
                                labelColor="#FFFFFF"
                                field={field}
                                disableLabel
                                disabled
                            />
                        )}
                    />
                    <span className={"error"}>
                        {errors.accountName?.message}
                    </span>

                </InputBox>

                <InputBox>
                    <Controller
                        name="description"
                        control={control}
                        rules={{
                            required: false,
                        }}
                        render={({ field }) => (
                            <Input
                                type="text"
                                label="Payment description (optional)"
                                field={field}
                                labelColor="#FFFFFF"
                                disableLabel
                            />
                        )}
                    />
                </InputBox>

                <ContinueButton>
                    <ItemWrapper>
                        <Button
                            onClick={handlePrevious}
                            label="Prev"
                            height="48px"
                            theme="alternate"
                            width="203px"
                        />

                        <Button
                            onClick={handleSubmit(onSubmit)}
                            label="Continue"
                            height="48px"
                            disabled={!isValid}
                        />
                    </ItemWrapper>
                </ContinueButton>

            </Form>
        </ContentWrapper>
    )
}

export default Beneficiary